// Afgeleid team voor Projectinfo (23 okt 2026, project 9604 FBTO).
//
// Cameraman, Editor, Productie en Animator zijn losse handmatige Airtable-velden op het project
// en staan vaak leeg, terwijl de tool het team al kent:
//   - Camera: de crew van de draaidagen (gekoppelde Personen) plus de namen in de Outlook-titel
//     ("Erwin(b) + Paul(b) + Enya || …") — externe freelancers staan alleen daar;
//   - Editor/Productie/Animator: de Airtable-rollups op het project (main, live Airtable) of,
//     als die er niet zijn (develop leest de PG-spiegel), de uitvoerders van de taken.
// Dit levert alleen VOORSTELLEN; overnemen in het handmatige veld doet de gebruiker. Wie geen
// Personen-record heeft, kan niet worden overgenomen (het veld is een record-link) en wordt
// alleen getoond.
import { crewSamengevoegd } from '../../../utils/draaidagCrew';

export const AFGELEIDE_ROLLEN = ['Cameraman', 'Editor', 'Productie', 'Animator'];

const ROLLUP = {
  Editor: 'ROLLUP - Editors',
  Productie: 'ROLLUP - Productie',
  Animator: 'ROLLUP - Animatoren',
};

// Taakregels voor als er geen rollup is — zelfde uitkomst als de rollups op 9604:
// Editors = Montage-taken van wie Editor is, Animatoren = Animatie/Illustratie-taken,
// Productie = wie als producer of productie(-assistent) op een taak staat.
const TAAK_REGELS = {
  Editor: { types: ['Montage'], functie: /\beditor\b/i },
  Animator: { types: ['Animatie', 'Illustratie'], functie: null },
  Productie: { types: null, functie: /\bproduc(er|tie)/i },
};
const CAMERA_FUNCTIE = /camera|regisseur|\bdop\b/i;

const lijst = (w) => (Array.isArray(w) ? w : w == null || w === '' ? [] : String(w).split(',')).map((x) => String(x).trim()).filter(Boolean);
const isRecordId = (s) => /^rec[A-Za-z0-9]{14}$/.test(String(s || ''));
const voornaam = (v) => String(v || '').trim().toLowerCase().split(/\s+/)[0];
const functieStr = (p) => {
  const raw = p?.functie ?? p?.Functie ?? '';
  return Array.isArray(raw) ? raw.join(', ') : String(raw || '');
};

function isActieveDraaidag(f = {}) {
  // `_actief` = genormaliseerd door de Node-route op main (Airtable laat lege vinkjes weg).
  return !(f._actief === false || f.is_valid === false || f.all_day === false || f.hidden === true || f.Verberg === true
    || f.outlook_link_status === false || f.OutlookLinkStatus === false);
}

/**
 * @param {object} p
 * @param {object} p.fields     projectvelden (handmatige rolvelden + eventuele rollups)
 * @param {Array}  p.tasks      taken van het project ({ id, fields })
 * @param {Array}  p.draaidagen draaidagen van het project ({ id, fields })
 * @param {Array}  p.personen   [{ id, naam, functie, type, voornaam }]
 * @param {boolean} p.titelMatch titelnamen op unieke voornaam aan een Personen-record koppelen
 *          (alleen met de VOLLEDIGE personenlijst, anders lijkt een naam ten onrechte uniek)
 * @returns {Record<string, {id: string|null, naam: string, status: string|null, bron: string}[]>}
 *          per rol de voorstellen die nog NIET in het handmatige veld staan.
 */
export function afgeleidTeam({ fields = {}, tasks = [], draaidagen = [], personen = [], titelMatch = true } = {}) {
  const perId = new Map((personen || []).map((p) => [p.id, p]));
  const perNaam = new Map((personen || []).map((p) => [String(p.naam || '').trim().toLowerCase(), p]));
  const uit = {};
  const zet = (rol, item) => {
    const rij = uit[rol] || (uit[rol] = []);
    const sleutel = item.id || `naam:${voornaam(item.naam)}`;
    if (rij.some((x) => (x.id || `naam:${voornaam(x.naam)}`) === sleutel)) return;
    // Een titelnaam die al als gekoppelde persoon voorkomt (zelfde voornaam) niet dubbel.
    if (!item.id && rij.some((x) => voornaam(x.naam) === voornaam(item.naam))) return;
    rij.push(item);
  };
  const vanId = (id, bron, reserveNaam) => {
    const p = perId.get(id);
    const naam = p?.naam || (reserveNaam && !isRecordId(reserveNaam) ? reserveNaam : '');
    return naam ? { id, naam, status: null, bron } : null;
  };

  // ── Camera: draaidag-crew + titelnamen ──
  (draaidagen || []).filter((d) => isActieveDraaidag(d.fields)).forEach((d) => {
    const f = d.fields || {};
    const ids = lijst(f.Crew).filter(isRecordId);
    const namen = lijst(f['Naam (from Crew)']).filter((n) => !isRecordId(n));
    const gekoppeld = [];
    if (ids.length) {
      ids.forEach((id, i) => { const it = vanId(id, 'draaidag', namen[i]); if (it) gekoppeld.push(it); });
    } else {
      namen.forEach((n) => {
        // De PG-spiegel (develop) bewaart vaak alleen voornamen ("Luuk"); een gekoppelde crew is
        // altijd een Personen-record, dus een unieke voornaam in de volledige lijst is hem.
        const alleenVoornaam = !/\s/.test(n.trim());
        const opVoornaam = titelMatch && alleenVoornaam ? (personen || []).filter((q) => voornaam(q.voornaam || q.naam) === voornaam(n)) : [];
        const p = perNaam.get(n.toLowerCase()) || (opVoornaam.length === 1 ? opVoornaam[0] : null);
        gekoppeld.push(p ? { id: p.id, naam: p.naam, status: null, bron: 'draaidag' } : { id: null, naam: n, status: null, bron: 'draaidag' });
      });
    }
    gekoppeld.forEach((it) => {
      const p = it.id ? perId.get(it.id) : null;
      // Producer/stagiair op de draaidag is geen camera; onbekende functie laten we staan.
      if (p && functieStr(p) && !CAMERA_FUNCTIE.test(functieStr(p))) return;
      zet('Cameraman', it);
    });
    // Titelnamen die niet gekoppeld zijn (externe crew). Een unieke voornaam onder de Personen
    // maakt hem overneembaar (met volledige naam, zodat je ziet wie je kiest).
    // titelNietGekoppeld: ook ongemarkeerde interne collega's uit de titel (nietGekoppeld = alleen te koppelen externe crew)
    const { titelNietGekoppeld: nietGekoppeld } = crewSamengevoegd(namen.length ? namen : gekoppeld.map((g) => g.naam), f.Draaidag);
    nietGekoppeld.forEach(({ naam, status }) => {
      const kandidaten = titelMatch ? (personen || []).filter((p) => voornaam(p.voornaam || p.naam) === voornaam(naam)) : [];
      const p = kandidaten.length === 1 ? kandidaten[0] : null;
      // Zelfde functiecontrole als bij gekoppelde crew: een producer in de titel is geen camera.
      if (p && functieStr(p) && !CAMERA_FUNCTIE.test(functieStr(p))) return;
      zet('Cameraman', p
        ? { id: p.id, naam: p.naam, status, bron: 'titel' }
        : { id: null, naam, status, bron: 'titel' });
    });
  });

  // ── Editor / Productie / Animator ──
  ['Editor', 'Productie', 'Animator'].forEach((rol) => {
    const rollupVeld = ROLLUP[rol];
    const rollup = lijst(fields?.[rollupVeld]).filter(isRecordId);
    if (Object.prototype.hasOwnProperty.call(fields || {}, rollupVeld)) {
      rollup.forEach((id) => { const it = vanId(id, 'rollup'); if (it) zet(rol, it); });
      if (rollup.length) return;
    }
    const regel = TAAK_REGELS[rol];
    (tasks || []).forEach((t) => {
      const tf = t.fields || {};
      if (tf.PrioNr === 'Gearchiveerd') return;
      if (regel.types && !regel.types.includes(tf.Type)) return;
      const ids = lijst(tf.Assignee).filter(isRecordId);
      const voornamen = lijst(tf['Voornaam Assignee']);
      const taakFunctie = lijst(tf['Functie (from Assignee)']).join(', ');
      ids.forEach((id, i) => {
        const p = perId.get(id);
        const fn = p ? functieStr(p) : taakFunctie;
        if (regel.functie && !regel.functie.test(fn)) return;
        const it = vanId(id, 'taken', voornamen[i]);
        if (it) zet(rol, it);
      });
    });
  });

  // Alleen wat nog niet in het handmatige veld staat.
  AFGELEIDE_ROLLEN.forEach((rol) => {
    const al = new Set(lijst(fields?.[rol]));
    const alNamen = new Set(lijst(fields?.[`Naam (from ${rol})`]).map(voornaam));
    if (uit[rol]) {
      uit[rol] = uit[rol].filter((x) => (x.id ? !al.has(x.id) : !alNamen.has(voornaam(x.naam))));
      if (!uit[rol].length) delete uit[rol];
    }
  });
  return uit;
}

export default afgeleidTeam;
